import * as React from "react";
import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	OutlinedInput,
	Stack,
	TextField,
} from "@mui/material";
import { CaretDown } from "@phosphor-icons/react/dist/ssr/CaretDown";
import { XIcon } from "@phosphor-icons/react/dist/ssr/X";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Campaign, useCopyCampaign, useCreateCampaign } from "@/hooks/api/use-campaigns";
import { ComponentLoader } from "@/components/componentLoader/component-loader";
import { toast } from "@/components/core/toaster";
import { CustomDatePicker } from "@/components/customDatePicker/custom-date-picker";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { FilterOption } from "@/components/customTable/types";
import { FilterFetchSelect } from "@/components/fetch-select";

export function CreateModal({
	open,
	onClose,
	campaignToCopy,
}: {
	open: boolean;
	onClose: () => void;
	campaignToCopy?: Campaign;
}): React.JSX.Element {
	const navigate = useNavigate();

	const isCopy = !!campaignToCopy;

	const createCampaignMutation = useCreateCampaign();
	const copyCampaignMutation = useCopyCampaign();

	const methods = useForm<any>({
		defaultValues: {
			title: "",
			timezone: campaignToCopy?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
			agentId: campaignToCopy?.agent?.id || "",
			agentTitle: campaignToCopy?.agent?.name || "",
			startAt: campaignToCopy?.startAt || "",
			endAt: campaignToCopy?.endAt || "",
		},
	});

	const {
		handleSubmit,
		reset,
		control,
		setValue,
		setError,
		clearErrors,
		watch,
		formState: { isSubmitting, errors },
	} = methods;

	React.useLayoutEffect(() => {
		if (campaignToCopy && isCopy) {
			reset({
				title: '',
				timezone: campaignToCopy.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
				agentId: campaignToCopy.agent?.id || "",
				agentTitle: campaignToCopy.agent?.name || "",
				startAt: campaignToCopy.startAt || "",
				endAt: campaignToCopy.endAt || "",
			});
		} else {
			reset({
				title: "",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				agentId: "",
				agentTitle: "",
				startAt: "",
				endAt: "",
			});
		}
	}, [campaignToCopy, isCopy, reset, open]);

	const handleClose = () => {
		setTimeout(() => {
			reset({
				title: "",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				agentId: "",
				agentTitle: "",
				startAt: "",
				endAt: "",
			});
			clearErrors();
		}, 300);

		onClose();
	};

	const agentId = watch("agentId");
	const agentTitle = watch("agentTitle");

	const onSubmitForm = async (data: any) => {
		if (!data.title) {
			setError("title", { message: "Title is required" });
			return;
		}

		try {
			const campaignData = {
				title: data.title,
				timezone: data.timezone,
				agentId: data.agentId || null,
				startAt: data.startAt || null,
				endAt: data.endAt || null,
			};

			const response = await (isCopy
				? copyCampaignMutation.mutateAsync({ campaignId: campaignToCopy.id, data: campaignData })
				: createCampaignMutation.mutateAsync(campaignData));

			handleClose();

			setTimeout(() => {
				navigate(`/campaigns/${response.id}`);
			}, 300);
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;
			if (!Array.isArray(errorMessages)) {
				toast.error(errorMessages);
				return;
			}
			const errorMessage = errorMessages[0];

			const key = errorMessage.split(" ")[0];

			if (["startAt", "endAt", "timezone", "title", "agent"].includes(key)) {
				setError(key, { message: errorMessage });
			}
		}
	};
	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				"& .MuiDialog-container": {
					"& .MuiPaper-root": {
						width: "100%",
						maxWidth: "560px",
					},
				},
			}}
		>
			<DialogTitle>
				{isCopy ? "Copy Campaign" : "Create Campaign"}
				<IconButton color="inherit" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
					<XIcon width={20} height={20} />
				</IconButton>
			</DialogTitle>
			<DialogContent sx={{ py: 4!, pt: "8px !important" }}>
				<ComponentLoader loading={createCampaignMutation.isPending} />
				<Grid container spacing={2}>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						{" "}
						<Controller
							control={control}
							name="title"
							rules={{
								required: "Title is required",
								minLength: {
									value: 2,
									message: "Title must be at least 2 characters",
								},
							}}
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth required>
									<InputLabel>Title</InputLabel>
									<OutlinedInput {...field} placeholder="Enter title" />
									{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid size={{ xs: 12, md: 12 }}>
						<Controller
							control={control}
							name="timezone"
							rules={{
								required: "Timezone is required",
							}}
							render={({ field }) => (
								<FormControl error={Boolean(errors.timezone)} fullWidth required>
									<Autocomplete
										options={Intl.supportedValuesOf("timeZone")}
										value={field.value || null}
										onChange={(_, newValue) => field.onChange(newValue)}
										popupIcon={<CaretDown size={18} />}
										slotProps={{
											chip: { color: "primary" },
											paper: {
												sx: {
													fontSize: "14px !important",
												},
											},
											popper: {
												sx: {
													"& .MuiAutocomplete-option": {
														margin: "4px 0 !important",
														fontSize: "14px !important",
													},
												},
											},
										}}
										sx={{
											width: "100%",
											"& .MuiInputBase-input": {
												cursor: "pointer",
												fontSize: "16px !important",
											},
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Timezone"
												placeholder="Select a timezone"
												error={Boolean(errors.timezone)}
												required
											/>
										)}
									/>
									{errors.timezone ? <CustomFormHelperError text={(errors.timezone.message as string) || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>

					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="agentId"
							render={() => (
								<FormControl error={Boolean(errors.agentId)} fullWidth>
									<FilterFetchSelect
										label="Agent"
										showInputLabel
										errorText={errors.agentId?.message as string}
										value={{ label: agentTitle, value: agentId } as FilterOption}
										placeholder="Select an agent"
										onChange={(selected: FilterOption | string) => {
											const value = typeof selected === "string" ? selected : (selected.value as string) || "";
											const label = typeof selected === "string" ? selected : (selected.label as string) || "";

											setValue("agentId", value);
											setValue("agentTitle", label);
										}}
										getOptionsUrl="/agents"
										fetchLabel="name"
										fetchValue="id"
									/>
									{errors.agentId ? <CustomFormHelperError text={(errors.agentId.message as string) || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 6,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="startAt"
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth>
									<CustomDatePicker
										{...field}
										label="Start At"
										minDate={dayjs()}
										setValue={field.onChange}
										error={Boolean(fieldState.error)}
									/>
									{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 6,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="endAt"
							render={({ field, fieldState }) => (
								<FormControl error={Boolean(fieldState.error)} fullWidth>
									<CustomDatePicker
										{...field}
										label="End At"
										minDate={dayjs()}
										setValue={field.onChange}
										error={Boolean(fieldState.error)}
									/>
									{fieldState.error && <CustomFormHelperError text={fieldState.error.message || ""} />}
								</FormControl>
							)}
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
				<Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
					<Button color="secondary" onClick={handleClose}>
						Cancel
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleSubmit(onSubmitForm)}
						disabled={isSubmitting || createCampaignMutation.isPending}
					>
						{isCopy ? "Copy Campaign" : "Create Campaign"}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
}
